/*
 * Copyright (c) 2024, King Living
 * Reusable Form Validation Utilities
 *
 * Provides validation rules for common form fields:
 * - Email validation
 * - Phone number validation (supports international formats)
 * - Name validation (firstName, lastName)
 *
 * These validators can be used with react-hook-form rules.
 */

import {normalizePostcodeForValidation} from '../constants/postal-patterns';
import {normalizePhoneDigitsForRegion} from '../constants/phone-region-patterns';
import {getRegionAddressConfig} from '../constants/region-address-config';

/**
 * Field character limits
 * Default values used when no specific limit is provided
 */
export const FIELD_LIMITS = {
  email: 256,
  phone: 20,
  firstName: 50,
  lastName: 50,
  address1: 100,
  address2: 100,
  city: 50,
  postalCode: 10,
  companyName: 50,
  deliveryInstructions: 500,
  orderNumber: 50,
  message: 5000,
};

/**
 * Email validation regex
 * Matches standard email format: local@domain.tld
 * - Local part: word chars, dots, %, +, -
 * - Domain: word chars, dots, -
 * - TLD: 2-6 characters
 */
export const EMAIL_REGEX = /^[\w.%+-]+@[\w.-]+\.[a-zA-Z]{2,6}$/;

/**
 * Phone number validation regex
 * Allows: digits, spaces, and + (for international codes)
 * Examples: +61 412 345 678, 0412 345 678, +1 555 123 4567
 */
export const PHONE_REGEX = /^[+\d\s]+$/;

/**
 * Job application phone format (JA-007 / JobAdder API).
 * E.164 (+country code, no leading 0) or local format starting with 0.
 * Examples: +61423432167, 0423432167
 */
export const APPLICATION_PHONE_REGEX = /^(\+?[1-9]\d{1,14}|0\d{9,10})$/;

/**
 * @param {string} value - Phone number (may include spaces or hyphens)
 * @returns {boolean}
 */
export function isValidApplicationPhone(value) {
  if (!value || typeof value !== 'string') {
    return false;
  }

  return APPLICATION_PHONE_REGEX.test(value.replace(/[\s-]/g, ''));
}

/**
 * Name validation patterns
 * - INVALID_NAME_CHARS: Special characters that are NOT allowed
 * - VALID_NAME_REGEX: Allows letters (including accented), apostrophes, hyphens, spaces,
 *   periods, and commas
 * - EMOJI_REGEX: Matches emoji characters
 * - NUMBER_REGEX: Matches any digit
 *
 * KL-1042 (client-approved FSD update, comment 1340538): name fields — first name, last name
 * and company name — exclude !"#$%&()*+/:;<=>?@[\]^_`{|}~ . Periods (.) and commas (,) are now
 * ALLOWED (previously blocked), so they are removed from INVALID_NAME_CHARS and added to
 * VALID_NAME_REGEX.
 */
export const INVALID_NAME_CHARS = /[!"#$%&()*+/:;<=>?@[\\\]^_`{|}~]/;
export const VALID_NAME_REGEX = /^[a-zA-ZÀ-ÿ',.\-\s]+$/;
export const EMOJI_REGEX = /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F700}-\u{1F77F}\u{1F780}-\u{1F7FF}\u{1F800}-\u{1F8FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/u;
export const NUMBER_REGEX = /\d/;

/** FSD-blocked special characters for generic text fields (default for validateAlphaNumericText) */
export const INVALID_TEXT_CHARS = /[!"#$%&()*+,./:;<=>?@[\\\]^_`{|}~]/;

/**
 * KL-1042 (client-approved FSD update, comment 1340538): the delivery instructions field accepts
 * most punctuation (., $, !, etc). Only these characters are blocked: < > \ ` ^ { | } ~
 */
export const INVALID_DELIVERY_CHARS = /[<>\\`^{|}~]/;

/**
 * Validate email address
 *
 * @param {string} value - Email to validate
 * @param {Object} options - Validation options
 * @param {number} [options.maxLength] - Maximum character length
 * @param {Function} [options.formatMessage] - i18n formatter function
 * @returns {string|true} - Error message or true if valid
 *
 * @example
 * rules: {
 *   validate: (value) => validateEmail(value, {formatMessage})
 * }
 */
export function validateEmail(value, options = {}) {
  const {maxLength = FIELD_LIMITS.email, formatMessage} = options;

  // Check if empty (use required rule for mandatory check)
  if (!value || typeof value !== 'string') {
    return true;
  }

  const trimmedValue = value.trim();

  // Check character limit
  if (trimmedValue.length > maxLength) {
    return formatMessage ?
      formatMessage({
        defaultMessage: 'Email must be {maxLength} characters or less',
        id: 'validation.error.email_max_length',
      }, {maxLength}) :
      `Email must be ${maxLength} characters or less`;
  }

  // Check email format
  if (!EMAIL_REGEX.test(trimmedValue)) {
    return formatMessage ?
      formatMessage({
        defaultMessage: 'Please enter a valid email address',
        id: 'validation.error.invalid_email',
      }) :
      'Please enter a valid email address';
  }

  return true;
}

/**
 * Validate phone number
 *
 * Allows: digits, spaces, and + for international country codes
 * Examples: +61 412 345 678, 0412345678, +1 555 123 4567
 *
 * @param {string} value - Phone number to validate
 * @param {Object} options - Validation options
 * @param {number} [options.maxLength] - Maximum character length
 * @param {number} [options.minDigits] - Minimum digit count (default: 8)
 * @param {Function} [options.formatMessage] - i18n formatter function
 * @param {RegExp} [options.regionPattern] - Region-specific validation pattern
 * @param {string} [options.countryCode] - ISO country; normalizes +country digit strings before region test
 * @returns {string|true} - Error message or true if valid
 *
 * @example
 * rules: {
 *   required: 'Phone is required',
 *   validate: (value) => validatePhone(value, {formatMessage})
 * }
 */
export function validatePhone(value, options = {}) {
  const {
    maxLength = FIELD_LIMITS.phone,
    minDigits = 8,
    formatMessage,
    regionPattern,
    countryCode,
  } = options;

  // Empty values are handled by react-hook-form `required` rules
  if (!value || typeof value !== 'string' || !value.trim()) {
    return true;
  }

  const trimmedValue = value.trim();

  // Check character limit
  if (trimmedValue.length > maxLength) {
    return formatMessage ?
      formatMessage({
        defaultMessage: 'Phone number must be {maxLength} characters or less',
        id: 'validation.error.phone_max_length',
      }, {maxLength}) :
      `Phone number must be ${maxLength} characters or less`;
  }

  // Check allowed characters (digits, spaces, +)
  if (!PHONE_REGEX.test(trimmedValue)) {
    return formatMessage ?
      formatMessage({
        defaultMessage: 'Phone number can only contain digits, spaces, and + for country code',
        id: 'validation.error.phone_invalid_chars',
      }) :
      'Phone number can only contain digits, spaces, and + for country code';
  }

  // Extract digits only for length validation
  const digits = trimmedValue.replace(/\D/g, '');

  // Check minimum digits
  if (digits.length < minDigits) {
    return formatMessage ?
      formatMessage({
        defaultMessage: 'Please enter a valid phone number',
        id: 'validation.error.phone_too_short',
      }) :
      'Please enter a valid phone number';
  }

  // Check region-specific pattern if provided (international +country formats normalized when countryCode set)
  if (regionPattern) {
    const digitsForRegion = countryCode ?
      normalizePhoneDigitsForRegion(digits, countryCode) :
      digits;

    if (!regionPattern.test(digitsForRegion)) {
      return formatMessage ?
        formatMessage({
          defaultMessage: 'Please enter a valid phone number for your region',
          id: 'validation.error.phone_invalid_region',
        }) :
        'Please enter a valid phone number for your region';
    }
  }

  return true;
}

/**
 * Required + regional phone validation for programmatic checks (outside react-hook-form).
 * Composes {@link validatePhone} with region config — same rules as address/profile fields.
 *
 * @param {string|null|undefined} value
 * @param {Object} [options]
 * @param {string} [options.countryCode='AU']
 * @param {Function} [options.formatMessage]
 * @param {{ id: string, defaultMessage: string }} [options.requiredMessage]
 * @returns {string|true}
 */
export function validatePhoneRequired(value, options = {}) {
  const {countryCode = 'AU', formatMessage, requiredMessage} = options;
  const activeCountry = countryCode || 'AU';
  const {phonePattern} = getRegionAddressConfig(activeCountry);

  if (!value || typeof value !== 'string' || !value.trim()) {
    if (requiredMessage && formatMessage) {
      return formatMessage(requiredMessage);
    }

    return formatMessage ?
      formatMessage({
        defaultMessage: 'Please enter your phone number.',
        id: 'validation.error.phone_required',
      }) :
      'Please enter your phone number.';
  }

  return validatePhone(value, {
    countryCode: activeCountry,
    formatMessage,
    maxLength: FIELD_LIMITS.phone,
    regionPattern: phonePattern,
  });
}

/**
 * Validate name field (firstName or lastName)
 *
 * Rules:
 * - Alpha characters only (including accented letters)
 * - Apostrophes allowed (e.g., O'Brien)
 * - Hyphens allowed (e.g., Mary-Jane)
 * - Spaces allowed (e.g., Mary Ann)
 * - Periods and commas allowed (e.g., "St. John", "Smith, Jr") — KL-1042
 * - Numbers NOT allowed
 * - Special characters NOT allowed: !"#$%&()*+/:;<=>?@[\]^_`{|}~
 * - Emojis NOT allowed
 *
 * @param {string} value - Name to validate
 * @param {Object} options - Validation options
 * @param {number} [options.maxLength] - Maximum character length
 * @param {string} [options.fieldName] - Field name for error messages (e.g., 'first name')
 * @param {Function} [options.formatMessage] - i18n formatter function
 * @returns {string|true} - Error message or true if valid
 *
 * @example
 * rules: {
 *   required: 'First name is required',
 *   validate: (value) => validateName(value, {fieldName: 'first name', formatMessage})
 * }
 */
export function validateName(value, options = {}) {
  const {
    maxLength = FIELD_LIMITS.firstName,
    fieldName = 'Name',
    formatMessage,
  } = options;

  // Check if empty (use required rule for mandatory check)
  if (!value || typeof value !== 'string') {
    return true;
  }

  const trimmedValue = value.trim();

  // Check character limit
  if (trimmedValue.length > maxLength) {
    return formatMessage ?
      formatMessage({
        defaultMessage: '{fieldName} must be {maxLength} characters or less',
        id: 'validation.error.name_max_length',
      }, {fieldName, maxLength}) :
      `${fieldName} must be ${maxLength} characters or less`;
  }

  // Check for numbers
  if (NUMBER_REGEX.test(trimmedValue)) {
    return formatMessage ?
      formatMessage({
        defaultMessage: '{fieldName} cannot contain numbers',
        id: 'validation.error.name_no_numbers',
      }, {fieldName}) :
      `${fieldName} cannot contain numbers`;
  }

  // Check for emojis
  if (EMOJI_REGEX.test(trimmedValue)) {
    return formatMessage ?
      formatMessage({
        defaultMessage: '{fieldName} cannot contain emojis',
        id: 'validation.error.name_no_emojis',
      }, {fieldName}) :
      `${fieldName} cannot contain emojis`;
  }

  // Check for invalid special characters
  if (INVALID_NAME_CHARS.test(trimmedValue)) {
    return formatMessage ?
      formatMessage({
        defaultMessage: '{fieldName} contains invalid characters',
        id: 'validation.error.name_invalid_chars',
      }, {fieldName}) :
      `${fieldName} contains invalid characters`;
  }

  // Validate against allowed pattern (letters, apostrophes, hyphens, spaces)
  if (!VALID_NAME_REGEX.test(trimmedValue)) {
    return formatMessage ?
      formatMessage({
        defaultMessage: '{fieldName} can only contain letters, apostrophes, and hyphens',
        id: 'validation.error.name_alpha_only',
      }, {fieldName}) :
      `${fieldName} can only contain letters, apostrophes, and hyphens`;
  }

  return true;
}

/**
 * Validate postcode / postal code / ZIP (empty allowed — use `required` separately)
 *
 * @param {string} value
 * @param {Object} options
 * @param {number} [options.maxLength]
 * @param {Function} [options.formatMessage]
 * @param {RegExp} [options.regionPattern]
 * @param {string} [options.fieldLabel]
 * @returns {string|true}
 */
export function validatePostcode(value, options = {}) {
  const {
    maxLength = FIELD_LIMITS.postalCode,
    formatMessage,
    regionPattern,
    fieldLabel = 'Postcode',
  } = options;

  if (!value || typeof value !== 'string' || !value.trim()) {
    return true;
  }

  const normalized = normalizePostcodeForValidation(value);

  if (normalized.length > maxLength) {
    return formatMessage ?
      formatMessage({
        defaultMessage: '{fieldLabel} must be {maxLength} characters or less',
        id: 'validation.error.postcode_max_length',
      }, {fieldLabel, maxLength}) :
      `${fieldLabel} must be ${maxLength} characters or less`;
  }

  if (regionPattern && !regionPattern.test(normalized)) {
    return formatMessage ?
      formatMessage({
        defaultMessage: 'Please enter a valid {fieldLabel}',
        id: 'validation.error.invalid_postcode',
      }, {fieldLabel}) :
      `Please enter a valid ${fieldLabel}`;
  }

  return true;
}

/**
 * Validate suburb/city field (address forms)
 *
 * Rules:
 * - Alpha characters and special characters allowed
 * - Numbers NOT allowed
 * - Emojis NOT allowed
 *
 * @param {string} value
 * @param {Object} options
 * @param {number} [options.maxLength]
 * @param {string} [options.fieldName]
 * @param {Function} [options.formatMessage]
 * @returns {string|true}
 */
export function validateSuburbCity(value, options = {}) {
  const {
    maxLength = FIELD_LIMITS.city,
    fieldName = 'Suburb',
    formatMessage,
  } = options;

  if (!value || typeof value !== 'string') {
    return true;
  }

  const trimmedValue = value.trim();

  const maxLengthResult = validateMaxLength(trimmedValue, {maxLength, fieldName, formatMessage});

  if (maxLengthResult !== true) {
    return maxLengthResult;
  }

  if (NUMBER_REGEX.test(trimmedValue)) {
    return formatMessage ?
      formatMessage({
        defaultMessage: '{fieldName} cannot contain numbers',
        id: 'validation.error.name_no_numbers',
      }, {fieldName}) :
      `${fieldName} cannot contain numbers`;
  }

  if (EMOJI_REGEX.test(trimmedValue)) {
    return formatMessage ?
      formatMessage({
        defaultMessage: '{fieldName} cannot contain emojis',
        id: 'validation.error.text_no_emojis',
      }, {fieldName}) :
      `${fieldName} cannot contain emojis`;
  }

  return true;
}

/**
 * Generic max-length validator for optional or required text fields
 *
 * @param {string} value
 * @param {Object} options
 * @param {number} [options.maxLength]
 * @param {string} [options.fieldName]
 * @param {Function} [options.formatMessage]
 * @returns {string|true}
 */
export function validateMaxLength(value, options = {}) {
  const {maxLength, fieldName = 'Field', formatMessage} = options;

  if (!value || typeof value !== 'string' || !maxLength) {
    return true;
  }

  const trimmedValue = value.trim();

  if (trimmedValue.length > maxLength) {
    return formatMessage ?
      formatMessage({
        defaultMessage: '{fieldName} must be {maxLength} characters or less',
        id: 'validation.error.field_max_length',
      }, {fieldName, maxLength}) :
      `${fieldName} must be ${maxLength} characters or less`;
  }

  return true;
}

/**
 * Text field that allows letters, numbers, and most punctuation; blocks emojis and a
 * configurable set of special characters.
 *
 * @param {string} value
 * @param {Object} options
 * @param {number} [options.maxLength]
 * @param {string} [options.fieldName]
 * @param {Function} [options.formatMessage]
 * @param {RegExp} [options.invalidChars] - Blocked-character set. Defaults to
 *   {@link INVALID_TEXT_CHARS}. KL-1042: name/company fields pass {@link INVALID_NAME_CHARS};
 *   delivery instructions pass {@link INVALID_DELIVERY_CHARS}.
 * @returns {string|true}
 */
export function validateAlphaNumericText(value, options = {}) {
  const {
    maxLength = FIELD_LIMITS.deliveryInstructions,
    fieldName = 'Field',
    formatMessage,
    invalidChars = INVALID_TEXT_CHARS,
  } = options;

  if (!value || typeof value !== 'string' || !value.trim()) {
    return true;
  }

  const trimmedValue = value.trim();

  const maxLengthResult = validateMaxLength(trimmedValue, {maxLength, fieldName, formatMessage});

  if (maxLengthResult !== true) {
    return maxLengthResult;
  }

  if (EMOJI_REGEX.test(trimmedValue)) {
    return formatMessage ?
      formatMessage({
        defaultMessage: '{fieldName} cannot contain emojis',
        id: 'validation.error.text_no_emojis',
      }, {fieldName}) :
      `${fieldName} cannot contain emojis`;
  }

  if (invalidChars.test(trimmedValue)) {
    return formatMessage ?
      formatMessage({
        defaultMessage: '{fieldName} contains invalid characters',
        id: 'validation.error.text_invalid_chars',
      }, {fieldName}) :
      `${fieldName} contains invalid characters`;
  }

  return true;
}

/**
 * Create validation rules for react-hook-form
 * Combines required rule with validate function
 *
 * @param {Object} options - Options
 * @param {string} options.requiredMessage - Required field error message
 * @param {Function} options.validator - Validation function
 * @returns {Object} - react-hook-form rules object
 */
export function createValidationRules({requiredMessage, validator}) {
  return {
    required: requiredMessage,
    validate: validator,
  };
}

/**
 * Sanitize phone number input
 * Removes invalid characters while preserving allowed ones (digits, spaces, +)
 *
 * @param {string} value - Input value
 * @returns {string} - Sanitized value
 */
export function sanitizePhone(value) {
  if (!value) return '';

  // Keep only digits, spaces, and +
  return value.replace(/[^\d\s+]/g, '');
}

/**
 * Sanitize name input
 * Removes invalid characters while preserving allowed ones
 *
 * @param {string} value - Input value
 * @returns {string} - Sanitized value
 */
export function sanitizeName(value) {
  if (!value) return '';

  // Remove numbers, emojis, and invalid special characters
  return value
    .replace(NUMBER_REGEX, '')
    .replace(EMOJI_REGEX, '')
    .replace(INVALID_NAME_CHARS, '');
}

/**
 * Sanitize name input while typing — blocks digits only (KINDW-1694).
 * Special characters are kept so validateName can surface errors (KINDW-1695).
 *
 * @param {string} value - Input value
 * @returns {string} - Sanitized value
 */
export function sanitizeNameNumbers(value) {
  if (!value) return '';

  return value.replace(NUMBER_REGEX, '');
}

/**
 * Wrap react-hook-form register field props to sanitize name input while typing.
 *
 * @param {Object} fieldRegistration - Result of register('fieldName', rules)
 * @returns {Object} Field props with sanitized onChange
 */
export function withNameInputSanitization(fieldRegistration) {
  const {onChange, ...field} = fieldRegistration;

  return {
    ...field,
    onChange(event) {
      onChange({
        ...event,
        target: {...event.target, value: sanitizeNameNumbers(event.target.value)},
      });
    },
  };
}

export default {
  FIELD_LIMITS,
  EMAIL_REGEX,
  PHONE_REGEX,
  APPLICATION_PHONE_REGEX,
  VALID_NAME_REGEX,
  INVALID_NAME_CHARS,
  INVALID_TEXT_CHARS,
  INVALID_DELIVERY_CHARS,
  validateEmail,
  validatePhone,
  validatePhoneRequired,
  isValidApplicationPhone,
  validateName,
  validatePostcode,
  validateSuburbCity,
  validateMaxLength,
  validateAlphaNumericText,
  createValidationRules,
  sanitizePhone,
  sanitizeName,
  sanitizeNameNumbers,
  withNameInputSanitization,
};
